import { FC } from 'react';
import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge, Tip } from '@components';
import { useContentDirection } from '@hooks';
import { type SingularKeys, Trans, useTrans } from '@libs/i18n';
import { Assessment, OverallResults } from '@libs/types';
import {
  ChartLineUpIcon,
  CheckCircleIcon,
  FilesIcon,
  ShieldWarningIcon,
} from '@phosphor-icons/react';

import { OverallResultsViewModel } from '../../types';

import { DeltaTag } from './DeltaTag';
import { StatCard } from './StatCard';

interface ResultsStatsCardsProps {
  results: OverallResultsViewModel;
  language: string;
  activeVersion?: Assessment;
  baselineVersion?: Assessment;
  comparisonOverall?: OverallResults;
  editedDomainsCount?: number;
  /** Called when user clicks the critical-gaps count — should switch to Detailed Gaps tab */
  onViewDetailedGaps?: () => void;
}

export const ResultsStatsCards: FC<ResultsStatsCardsProps> = observer(({
  results,
  language,
  activeVersion,
  baselineVersion,
  comparisonOverall,
  editedDomainsCount,
  onViewDetailedGaps,
}) => {
  const theme = useTheme();
  const { i18n, t } = useTrans();
  // Results are AI prose written once, in the assessment's language. Name that
  // language in the reader's own language so the badge doesn't itself become a
  // piece of untranslated English.
  const { lang: contentLang } = useContentDirection();
  const contentLanguageName =
    contentLang === 'ar' || contentLang === 'en'
      ? t(`assessments.overview.contentLanguage.${contentLang}` as SingularKeys)
      : language;

  const { maturityConfig, maturityScore, maturityLevel, maxMaturityLevel } = results;
  const maturityColor = maturityConfig.color.hex;
  const maturityLevelName = i18n.language === 'ar' ? maturityConfig.label.ar : maturityConfig.label.en;

  const formattedDate = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(results.processedAt);

  const isReassessment = !!activeVersion && activeVersion.version > 1;
  const baselineLabel = baselineVersion ? `V${baselineVersion.version}` : '';
  const showDeltas = !!comparisonOverall && !!baselineVersion;
  const baselineMaturity = comparisonOverall?.maturity.percentage ?? 0;
  const baselineGaps = comparisonOverall?.gaps.totalCount ?? 0;
  const baselineCriticalGaps = comparisonOverall?.gaps.criticalCount ?? 0;

  const showEditedBadge =
    showDeltas && !!editedDomainsCount && editedDomainsCount > 0;

  return (
    <Paper
      variant="panel"
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[300]}` }}
    >
      {/* Card header: "Overall Results" label + Compare dropdown */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography
          variant="textSM_semiBold"
          sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11.5 }}
        >
          <Trans i18nKey="assessments.overview.overallResults" />
        </Typography>

      </Box>

      {/* Completion Banner Section */}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1.75 }}>
        {/* Icon */}
        <Box sx={{ backgroundColor: 'green.200', p: 2.125, borderRadius: '100%', display: 'flex' }}>
          <CheckCircleIcon color={theme.palette.green[700]} size={32} />
        </Box>

        {/* Content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Typography variant="textMD_medium" sx={{ color: 'green.800' }}>
            {isReassessment ? (
              <Trans i18nKey="assessments.overview.versioning.hero.reassessmentCompleted" />
            ) : (
              <Trans i18nKey="assessments.overview.assessmentCompletedSuccessfully" />
            )}
          </Typography>
          <Typography variant="textSM_regular">
            <Trans
              i18nKey="assessments.overview.completionSummary"
              values={{ gaps: results.totalGaps, controlPoints: results.totalArticlesAssessed }}
            />
          </Typography>

          {/* Language, date, and domains-edited row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Badge
              label={t('assessments.overview.resultsGeneratedIn', {
                language: contentLanguageName,
              })}
              variant="DomainName"
              sx={{ backgroundColor: 'blue.200', borderColor: 'blue.300' }}
            />
            <Tip
              title={t('assessments.overview.resultsGeneratedInHelp', {
                language: contentLanguageName,
              })}
            />
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              <Trans i18nKey="assessments.overview.completedOn" values={{ date: formattedDate }} />
            </Typography>
            {showEditedBadge && (
              <>
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>·</Typography>
                <Chip
                  size="small"
                  label={t('assessments.overview.versioning.hero.domainsEditedFromPrev', {
                    count: editedDomainsCount,
                    version: baselineLabel,
                  })}
                  sx={{
                    height: 20,
                    fontSize: 11.5,
                    fontWeight: 600,
                    backgroundColor: '#FAF5FD',
                    color: theme.palette.violet[700],
                    border: '1px solid #E8D4F8',
                    borderRadius: 999,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Stats Cards Grid */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, flexWrap: 'wrap', mt: 3 }}>
        {/* Overall Maturity Score Card */}
        <StatCard
          title={<Trans i18nKey="assessments.overview.overallMaturityScore" />}
          value={`${maturityScore.toFixed(0)}%`}
          valueColor={maturityColor}
          showProgressBar
          progressValue={maturityScore}
          progressColor={maturityColor}
          baselineProgressValue={showDeltas ? Math.round(baselineMaturity) : undefined}
          additionalContent={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
              {showDeltas && (
                <DeltaTag
                  now={Math.round(maturityScore)}
                  prev={Math.round(baselineMaturity)}
                  baselineLabel={baselineLabel}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: maturityColor }} />
                <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
                  <Trans
                    i18nKey="assessments.overview.maturityLevel"
                    values={{ levelName: maturityLevelName, level: maturityLevel, maxLevel: maxMaturityLevel }}
                  />
                </Typography>
              </Box>
            </Box>
          }
        />

        {/* Articles Assessed Card */}
        <StatCard
          title={<Trans i18nKey="assessments.overview.articlesAssessed" />}
          value={43}
          valueColor={theme.palette.text.primary}
          additionalContent={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChartLineUpIcon size={20} color={theme.palette.violet[700]} />
                <Typography variant="textSM_regular" sx={{ color: 'violet.700' }}>
                  <Trans i18nKey="assessments.overview.domainsCovered" values={{ count: results.domainsCovered }} />
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilesIcon size={20} color={theme.palette.blue[700]} />
                <Typography variant="textSM_regular" sx={{ color: 'blue.700' }}>
                  <Trans i18nKey="assessments.overview.documentsAssessed" values={{ count: results.documentsAssessed }} />
                </Typography>
              </Box>
            </Box>
          }
        />

        {/* Gaps card — single card, total + critical inline, clickable */}
        <StatCard
          title={<Trans i18nKey="assessments.overview.gapsIdentified" />}
          value={results.totalGaps}
          valueColor={theme.palette.error.main}
          subtitle={<Trans i18nKey="assessments.overview.gapsIdentifiedSubtitle" />}
          onClick={onViewDetailedGaps}
          additionalContent={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
              {showDeltas && (
                <DeltaTag now={results.totalGaps} prev={baselineGaps} baselineLabel={baselineLabel} invertColor />
              )}

              {/* Critical gaps inline badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  border: `1px solid ${results.criticalGaps > 0
                    ? `${theme.palette.error.main}40`
                    : theme.palette.grey[200]}`,
                  backgroundColor: results.criticalGaps > 0
                    ? `${theme.palette.error.main}08`
                    : theme.palette.grey[50],
                  alignSelf: 'flex-start',
                }}
              >
                <ShieldWarningIcon
                  size={13}
                  color={results.criticalGaps > 0
                    ? theme.palette.error.main
                    : theme.palette.text.disabled as string}
                  weight="fill"
                />
                <Typography
                  variant="textSM_semiBold"
                  sx={{ color: results.criticalGaps > 0 ? theme.palette.error.main : 'text.disabled' }}
                >
                  {results.criticalGaps} <Trans i18nKey="assessments.overview.gaps.critical" />
                </Typography>
              </Box>

              {showDeltas && (
                <DeltaTag now={results.criticalGaps} prev={baselineCriticalGaps} baselineLabel={baselineLabel} invertColor />
              )}

              {onViewDetailedGaps && (
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                  <Trans i18nKey="assessments.overview.gaps.viewDetailed" />
                </Typography>
              )}
            </Box>
          }
        />

        {/* Missing Documents Card */}
        <StatCard
          title={<Trans i18nKey="assessments.overview.missingDocuments" />}
          value={results.missingDocuments}
          valueColor={theme.palette.error.main}
          subtitle={<Trans i18nKey="assessments.overview.acrossAllDomains" />}
          additionalContent={
            showDeltas ? (
              <Box sx={{ mt: 0.5 }}>
                <DeltaTag
                  now={results.missingDocuments}
                  prev={comparisonOverall?.missingDocuments ?? 0}
                  baselineLabel={baselineLabel}
                  invertColor
                />
              </Box>
            ) : null
          }
        />

      </Box>
    </Paper>
  );
});
